import { IconButton, Menu, MenuItem } from "@mui/material";
import DataTable from "app/components/DataTable";
import { ThreedotIcon, UpArrowIcon, DownArrowIcon } from "app/icons";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { selectManufacturersReordering } from "../../redux/selector";
import { manufacturerActions } from "../../redux/slice";
import { Manufacturer, ManufacturerReorderDirection } from "../../types";
import { useStyles } from "./style";

type Props = {
  rows: Manufacturer[];
  loading?: boolean;
};

const ManufacturerTable = ({ rows, loading = false }: Props) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const reordering = useSelector(selectManufacturersReordering);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const closeMenu = useCallback(() => {
    setMenuAnchor(null);
    setSelectedId(null);
  }, []);

  const openMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, manufacturer: Manufacturer) => {
      event.stopPropagation();
      setMenuAnchor(event.currentTarget);
      setSelectedId(manufacturer.menufecturerID);
    },
    []
  );

  const handleMove = useCallback(
    (direction: ManufacturerReorderDirection) => {
      if (selectedId === null) {
        return;
      }

      dispatch(
        manufacturerActions.reorderManufacturer({ id: selectedId, direction })
      );
      closeMenu();
    },
    [closeMenu, dispatch, selectedId]
  );

  const columns = useMemo(
    () => [
      {
        id: "menu",
        label: "",
        align: "left",
        format: (row: Manufacturer) => (
          <IconButton
            size="small"
            className={styles.menuButton}
            aria-label={t("MANUFACTURERS.TABLE.ROW_ACTIONS")}
            onClick={(event) => openMenu(event, row)}
          >
            <ThreedotIcon />
          </IconButton>
        ),
      },
      {
        id: "menufecturerName",
        label: t("MANUFACTURERS.TABLE.MANUFACTURER_NAME"),
        width: 180,
        minWidth: 180,
        maxWidth: 220,
        align: "right",
      },
      {
        id: "serial",
        label: "#",
        width: 48,
        align: "right",
        format: (_row: Manufacturer, index: number) => index + 1,
      },
    ],
    [openMenu, styles, t]
  );

  return (
    <div className={styles.root}>
      <DataTable
        rows={rows}
        columns={columns}
        loading={loading && rows.length === 0}
        serialEnabled={false}
        noDataLabel={t("MANUFACTURERS.EMPTY")}
      />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        classes={{ paper: styles.menuPaper }}
        disableScrollLock
      >
        <MenuItem
          className={styles.menuItem}
          disabled={reordering}
          onClick={() => handleMove("up")}
        >
          <span className={styles.menuItemLabel}>
            {t("MANUFACTURERS.TABLE.MOVE_UP")}
          </span>
          <span className={styles.menuItemIcon} aria-hidden>
            <UpArrowIcon width={20} height={20} />
          </span>
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          disabled={reordering}
          onClick={() => handleMove("down")}
        >
          <span className={styles.menuItemLabel}>
            {t("MANUFACTURERS.TABLE.MOVE_DOWN")}
          </span>
          <span className={styles.menuItemIcon} aria-hidden>
            <DownArrowIcon width={20} height={20} />
          </span>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default ManufacturerTable;
